
import React, { useState } from 'react';
import { format, subDays, addDays, startOfMonth, endOfMonth, eachDayOfInterval, startOfWeek, endOfWeek } from 'date-fns';
import { useTeacherAttendance } from '../../hooks/useTeacherAttendance';
import { useAttendanceStore } from '../../store/attendanceStore';
import { useTimetableStore } from '../../store/timetableStore';
import { Card, CardContent, CardHeader } from '../ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { toast } from 'sonner';
import AttendanceChart from './chart/AttendanceChart';
import AttendanceReportHeader from './report/AttendanceReportHeader';
import PeriodNavigation from './report/PeriodNavigation';
import StatsSummary from './report/StatsSummary';
import { getCommonPrintStyles, getHeaderHtml, getFooterHtml, getSignatureHtml } from '@/utils/printing/printStyles';
import { downloadAsJPG, createPrintDocument } from '@/utils/printing/printingBridge';

const AttendanceReport = () => {
  const [period, setPeriod] = useState('week');
  const [currentDate, setCurrentDate] = useState(new Date());
  const { getAttendanceStats } = useTeacherAttendance();
  const { attendanceRecords } = useAttendanceStore();
  const { timetableData } = useTimetableStore();
  
  const handlePrevious = () => {
    if (period === 'day') {
      setCurrentDate(prev => subDays(prev, 1));
    } else if (period === 'week') {
      setCurrentDate(prev => subDays(prev, 7));
    } else if (period === 'month') {
      const prevMonth = new Date(currentDate);
      prevMonth.setMonth(currentDate.getMonth() - 1);
      setCurrentDate(prevMonth);
    }
  };
  
  const handleNext = () => {
    if (period === 'day') {
      setCurrentDate(prev => addDays(prev, 1));
    } else if (period === 'week') {
      setCurrentDate(prev => addDays(prev, 7));
    } else if (period === 'month') {
      const nextMonth = new Date(currentDate);
      nextMonth.setMonth(currentDate.getMonth() + 1);
      setCurrentDate(nextMonth);
    }
  };

  const handleExportReport = async () => {
    try {
      const loadingToast = toast.loading('Generating attendance report JPG...');

      const reportData = generateRealReportData();
      const title = `${period.charAt(0).toUpperCase() + period.slice(1)} Attendance Report`;
      
      let content = `
        <div class="content">
          <div class="report-header">
            <h2>Attendance Report - ${period.charAt(0).toUpperCase() + period.slice(1)}</h2>
            <p>Period: ${formatPeriodDisplay()}</p>
            <p>Generated on: ${format(new Date(), 'PPP')}</p>
          </div>
          
          <table class="attendance-table">
            <thead>
              <tr>
                <th>Date/Period</th>
                <th>Present</th>
                <th>Late</th>
                <th>Absent</th>
                <th>Total</th>
              </tr>
            </thead>
            <tbody>
      `;

      reportData.forEach(data => {
        const total = data.present + data.late + data.absent;
        content += `
          <tr>
            <td>${data.label}</td>
            <td class="status-present">${data.present}</td>
            <td class="status-late">${data.late}</td>
            <td class="status-absent">${data.absent}</td>
            <td><strong>${total}</strong></td>
          </tr>
        `;
      });

      const totalPresent = reportData.reduce((sum, data) => sum + data.present, 0);
      const totalLate = reportData.reduce((sum, data) => sum + data.late, 0);
      const totalAbsent = reportData.reduce((sum, data) => sum + data.absent, 0);
      const grandTotal = totalPresent + totalLate + totalAbsent;

      content += `
            </tbody>
          </table>
          
          <div class="summary-section">
            <h3>Summary Statistics</h3>
            <div class="stats-grid">
              <div class="stat-item">
                <span class="stat-label">Total Present:</span>
                <span class="stat-value status-present">${totalPresent}</span>
              </div>
              <div class="stat-item">
                <span class="stat-label">Total Late:</span>
                <span class="stat-value status-late">${totalLate}</span>
              </div>
              <div class="stat-item">
                <span class="stat-label">Total Absent:</span>
                <span class="stat-value status-absent">${totalAbsent}</span>
              </div>
              <div class="stat-item">
                <span class="stat-label">Grand Total:</span>
                <span class="stat-value"><strong>${grandTotal}</strong></span>
              </div>
            </div>
            
            ${grandTotal > 0 ? `
              <div class="percentage-stats">
                <div class="percentage-item">
                  <span class="percentage-label">Attendance Rate:</span>
                  <span class="percentage-value status-present">${Math.round((totalPresent / grandTotal) * 100)}%</span>
                </div>
                <div class="percentage-item">
                  <span class="percentage-label">Late Rate:</span>
                  <span class="percentage-value status-late">${Math.round((totalLate / grandTotal) * 100)}%</span>
                </div>
                <div class="percentage-item">
                  <span class="percentage-label">Absence Rate:</span>
                  <span class="percentage-value status-absent">${Math.round((totalAbsent / grandTotal) * 100)}%</span>
                </div>
              </div>
            ` : ''}
          </div>
        </div>
      `;

      const styles = `
        ${getCommonPrintStyles()}
        
        .report-header {
          text-align: center;
          margin-bottom: 30px;
        }
        .report-header h2 {
          font-size: 1.5rem;
          color: #3D7069;
          margin-bottom: 10px;
        }
        .report-header p {
          color: #666;
          font-size: 1rem;
          margin: 5px 0;
        }
        .attendance-table {
          width: 100%;
          border-collapse: collapse;
          margin-bottom: 30px;
        }
        .attendance-table th,
        .attendance-table td {
          border: 1px solid #ddd;
          padding: 12px;
          text-align: center;
        }
        .attendance-table th {
          background-color: #f0f0f0;
          font-weight: bold;
        }
        .attendance-table tr:nth-child(even) {
          background-color: #f9f9f9;
        }
        .status-present {
          color: #22c55e;
          font-weight: bold;
        }
        .status-absent {
          color: #ef4444;
          font-weight: bold;
        }
        .status-late {
          color: #f59e0b;
          font-weight: bold;
        }
        .summary-section {
          margin-top: 30px;
        }
        .summary-section h3 {
          color: #3D7069;
          margin-bottom: 15px;
        }
        .stats-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 15px;
          margin-bottom: 20px;
        }
        .stat-item {
          display: flex;
          justify-content: space-between;
          padding: 10px;
          background: #f8f9fa;
          border-radius: 5px;
        }
        .stat-label {
          font-weight: 500;
        }
        .stat-value {
          font-weight: bold;
        }
        .percentage-stats {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 15px;
          margin-top: 20px;
        }
        .percentage-item {
          display: flex;
          justify-content: space-between;
          padding: 10px;
          background: #e8f5e8;
          border-radius: 5px;
          border: 1px solid #c8e6c9;
        }
        .percentage-label {
          font-weight: 500;
        }
        .percentage-value {
          font-weight: bold;
        }
        .header-logo {
          display: block !important;
          visibility: visible !important;
          opacity: 1 !important;
        }
      `;

      // Generate the complete print document
      const printDocument = createPrintDocument(
        `${getHeaderHtml(title)}${content}${getSignatureHtml('report')}${getFooterHtml('report')}`, 
        `Attendance_Report_${period}_${format(currentDate, 'yyyy-MM-dd')}`, 
        styles
      );
      
      // Download as JPG
      await downloadAsJPG(printDocument, `Attendance_Report_${period}_${format(currentDate, 'yyyy-MM-dd')}`, {
        onError: (error) => {
          console.error('JPG download error:', error);
          toast.error('Failed to download attendance report');
        }
      });
      
      toast.dismiss(loadingToast);
      toast.success('Attendance report downloaded as JPG!');

    } catch (error) {
      console.error('Error exporting attendance report:', error);
      toast.error('Failed to export attendance report');
    }
  };

  const formatPeriodDisplay = () => {
    if (period === 'day') {
      return format(currentDate, 'EEEE, MMMM d, yyyy');
    } else if (period === 'week') {
      const weekStart = startOfWeek(currentDate, { weekStartsOn: 0 });
      const weekEnd = endOfWeek(currentDate, { weekStartsOn: 0 });
      return `${format(weekStart, 'MMM d')} - ${format(weekEnd, 'MMM d, yyyy')}`;
    } else if (period === 'month') {
      return format(currentDate, 'MMMM yyyy');
    }
    return '';
  };

  const generateRealReportData = () => {
    let dates = [];
    let labels = [];
    
    if (period === 'day') {
      dates = [currentDate];
      labels = [format(currentDate, 'EEEE')];
    } else if (period === 'week') {
      const weekStart = startOfWeek(currentDate, { weekStartsOn: 0 });
      const weekEnd = endOfWeek(currentDate, { weekStartsOn: 0 });
      dates = eachDayOfInterval({ start: weekStart, end: weekEnd });
      labels = dates.map(date => format(date, 'EEE'));
    } else if (period === 'month') {
      const monthStart = startOfMonth(currentDate);
      const monthEnd = endOfMonth(currentDate);
      dates = eachDayOfInterval({ start: monthStart, end: monthEnd });
      labels = dates.map(date => format(date, 'd'));
    }

    return dates.map((date, index) => {
      const dateStr = format(date, 'yyyy-MM-dd');
      const dayAttendance = attendanceRecords.filter(record => 
        record.date.startsWith(dateStr)
      );

      const present = dayAttendance.filter(record => record.status === 'present').length;
      const absent = dayAttendance.filter(record => record.status === 'absent').length;
      const late = dayAttendance.filter(record => record.status === 'late').length;

      return {
        label: labels[index],
        date: dateStr,
        present,
        absent,
        late
      };
    });
  };

  const generateChartData = () => {
    const reportData = generateRealReportData();
    
    return {
      labels: reportData.map(data => data.label),
      datasets: [
        {
          label: 'Present',
          data: reportData.map(data => data.present),
          borderColor: 'rgb(34, 197, 94)',
          backgroundColor: 'rgba(34, 197, 94, 0.2)',
          tension: 0.2,
        },
        {
          label: 'Late',
          data: reportData.map(data => data.late),
          borderColor: 'rgb(234, 179, 8)',
          backgroundColor: 'rgba(234, 179, 8, 0.2)',
          tension: 0.2,
        },
        {
          label: 'Absent',
          data: reportData.map(data => data.absent),
          borderColor: 'rgb(239, 68, 68)',
          backgroundColor: 'rgba(239, 68, 68, 0.2)',
          tension: 0.2,
        },
      ],
    };
  };
  
  const chartData = generateChartData();
  
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom' as const,
      },
      tooltip: {
        mode: 'index' as const,
        intersect: false,
      },
    },
    hover: {
      mode: 'index' as const,
      intersect: false,
    },
    scales: {
      x: {
        title: {
          display: true,
          text: period === 'day' ? 'Day' : period === 'week' ? 'Day of Week' : 'Day of Month',
        },
      },
      y: {
        title: {
          display: true,
          text: 'Number of Teachers',
        },
        beginAtZero: true,
      },
    },
  };

  return (
    <Card className="w-full">
      <CardHeader>
        <AttendanceReportHeader onExport={handleExportReport} />
      </CardHeader>
      <CardContent>
        <Tabs value={period} onValueChange={setPeriod} className="mb-6">
          <TabsList className="grid grid-cols-3 w-full max-w-md mx-auto">
            <TabsTrigger value="day">Daily</TabsTrigger>
            <TabsTrigger value="week">Weekly</TabsTrigger>
            <TabsTrigger value="month">Monthly</TabsTrigger>
          </TabsList>
        </Tabs>
        
        <PeriodNavigation
          currentDate={currentDate}
          period={period}
          onPrevious={handlePrevious}
          onNext={handleNext}
        />
        
        <AttendanceChart
          chartData={chartData}
          chartOptions={chartOptions}
        />
        
        <StatsSummary chartData={chartData} />
      </CardContent>
    </Card>
  );
};

export default AttendanceReport;
